import React, { useEffect, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Mail, Star, StarOff, Check, X, Loader2, Send, Pencil, Trash2, Plus, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import {
  listSenderProfiles,
  createSenderProfile,
  updateSenderProfile,
  deleteSenderProfile,
  setDefaultSenderProfile,
  testSenderProfile,
} from '../../lib/campaignsApi';

const Field = ({ label, children, help }) => (
  <div className="mb-3">
    <label className="block text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1">{label}</label>
    {children}
    {help && <div className="text-[11px] text-white/40 mt-1">{help}</div>}
  </div>
);

const TextInput = (props) => (
  <input
    {...props}
    className={`w-full px-3 py-2 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm ${props.className || ''}`}
  />
);

const ProfileRow = ({ p, onEdit, onSetDefault, onDelete, onTest }) => (
  <div
    className={`border ${p.is_default ? 'border-[#00D4FF]/40' : 'border-white/10'} bg-[#0A0A0A] p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3`}
    data-testid={`sender-profile-row-${p.key}`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-white font-semibold" data-testid={`sender-display-name-${p.key}`}>{p.display_name}</div>
        {p.is_default && (
          <span className="text-[10px] tracking-widest uppercase bg-[#00D4FF] text-black px-2 py-0.5" data-testid={`sender-default-badge-${p.key}`}>
            Default
          </span>
        )}
        {!p.is_active && (
          <span className="text-[10px] tracking-widest uppercase bg-white/10 text-white/60 px-2 py-0.5">
            Inactive
          </span>
        )}
      </div>
      <div className="text-white/60 text-sm mt-0.5" data-testid={`sender-email-${p.key}`}>{p.email}</div>
      <div className="text-white/40 text-xs mt-1 max-w-[640px]">{p.purpose}</div>
      <div className="text-[11px] text-white/40 mt-2 flex flex-wrap gap-3">
        <span>SMTP: {p.smtp_host}:{p.smtp_port}</span>
        <span>User: {p.smtp_user}</span>
        <span className={p.smtp_password_set ? 'text-emerald-400' : 'text-amber-400'}>
          {p.smtp_password_set ? '● Password set' : '○ Using global SMTP_PASSWORD'}
        </span>
      </div>
    </div>
    <div className="flex gap-2 flex-wrap">
      <button
        onClick={() => onTest(p)}
        className="px-3 py-1.5 text-xs border border-white/10 hover:border-[#00D4FF]/40 text-white/80 inline-flex items-center gap-1.5"
        data-testid={`sender-test-btn-${p.key}`}
      >
        <Send className="w-3.5 h-3.5" /> Test
      </button>
      {!p.is_default && p.is_active && (
        <button
          onClick={() => onSetDefault(p)}
          className="px-3 py-1.5 text-xs border border-white/10 hover:border-[#00D4FF]/40 text-white/80 inline-flex items-center gap-1.5"
          data-testid={`sender-set-default-btn-${p.key}`}
        >
          <Star className="w-3.5 h-3.5" /> Set Default
        </button>
      )}
      <button
        onClick={() => onEdit(p)}
        className="px-3 py-1.5 text-xs border border-white/10 hover:border-[#00D4FF]/40 text-white/80 inline-flex items-center gap-1.5"
        data-testid={`sender-edit-btn-${p.key}`}
      >
        <Pencil className="w-3.5 h-3.5" /> Edit
      </button>
      {!p.is_default && (
        <button
          onClick={() => onDelete(p)}
          className="px-3 py-1.5 text-xs border border-red-400/20 hover:border-red-400 text-red-400 inline-flex items-center gap-1.5"
          data-testid={`sender-delete-btn-${p.key}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  </div>
);

const EditModal = ({ profile, onClose, onSaved }) => {
  const isNew = !profile?.id;
  const [form, setForm] = useState({
    key: profile?.key || '',
    display_name: profile?.display_name || '',
    email: profile?.email || '',
    purpose: profile?.purpose || '',
    smtp_user: profile?.smtp_user || '',
    smtp_password: '',
    smtp_host: profile?.smtp_host || '',
    smtp_port: profile?.smtp_port || 465,
    is_active: profile?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const change = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    try {
      setSaving(true);
      if (isNew) {
        await createSenderProfile(form);
        toast.success('Sender profile created');
      } else {
        // Only send the password field if user entered something OR explicitly cleared it via the "Clear" button.
        const payload = { ...form };
        if (form.smtp_password === '') delete payload.smtp_password;
        await updateSenderProfile(profile.id, payload);
        toast.success('Sender profile updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="sender-edit-modal">
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-[560px] max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="text-white font-semibold">{isNew ? 'New Sender Profile' : `Edit: ${profile.display_name}`}</div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="sender-edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">
          {isNew && (
            <Field label="Key (slug, used internally)" help="Lowercase letters, digits, dashes only. Cannot be changed later.">
              <TextInput
                value={form.key}
                onChange={(e) => change('key', e.target.value.toLowerCase())}
                placeholder="e.g. inventory or marketing"
                data-testid="sender-key-input"
              />
            </Field>
          )}
          <Field label="Display Name" help="Shown in recipient inboxes, e.g. 'Luxor Tek Sales'">
            <TextInput
              value={form.display_name}
              onChange={(e) => change('display_name', e.target.value)}
              data-testid="sender-displayname-input"
            />
          </Field>
          <Field label="From Email" help="Must be a real mailbox on Hostinger for DKIM alignment">
            <TextInput
              type="email"
              value={form.email}
              onChange={(e) => change('email', e.target.value)}
              data-testid="sender-email-input"
            />
          </Field>
          <Field label="Purpose / Notes" help="Internal note: which kinds of campaigns use this sender">
            <textarea
              value={form.purpose}
              onChange={(e) => change('purpose', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm"
              data-testid="sender-purpose-input"
            />
          </Field>

          <div className="border-t border-white/5 pt-4 mt-4">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-3">SMTP Credentials (Hostinger)</div>
            <Field label="SMTP User" help="Usually the same as the From Email">
              <TextInput
                value={form.smtp_user}
                onChange={(e) => change('smtp_user', e.target.value)}
                placeholder={form.email || 'mailbox@luxortek.com'}
                data-testid="sender-smtpuser-input"
              />
            </Field>
            <Field
              label={isNew ? 'SMTP Password' : 'SMTP Password (leave blank to keep current)'}
              help={profile?.smtp_password_set
                ? 'Password is set. Type to replace, or clear to fall back to global SMTP_PASSWORD.'
                : 'No password set — currently falls back to global SMTP_PASSWORD from .env.'}
            >
              <div className="relative">
                <TextInput
                  type={showPw ? 'text' : 'password'}
                  value={form.smtp_password}
                  onChange={(e) => change('smtp_password', e.target.value)}
                  placeholder={profile?.smtp_password_set ? '••••••••' : 'Hostinger mailbox password'}
                  data-testid="sender-smtppass-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute top-1/2 -translate-y-1/2 right-2 text-white/40 hover:text-white"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="SMTP Host" help="Blank = use global SMTP_HOST">
                <TextInput
                  value={form.smtp_host}
                  onChange={(e) => change('smtp_host', e.target.value)}
                  placeholder="smtp.hostinger.com"
                  data-testid="sender-smtphost-input"
                />
              </Field>
              <Field label="SMTP Port" help="465 (SSL) or 587 (TLS)">
                <TextInput
                  type="number"
                  value={form.smtp_port}
                  onChange={(e) => change('smtp_port', parseInt(e.target.value || '465', 10))}
                  data-testid="sender-smtpport-input"
                />
              </Field>
            </div>
          </div>

          {!isNew && (
            <div className="border-t border-white/5 pt-4 mt-4">
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => change('is_active', e.target.checked)}
                  data-testid="sender-active-checkbox"
                />
                Active (uncheck to hide from campaign dropdown without deleting)
              </label>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-5">
            <button onClick={onClose} className="btn-secondary text-sm" data-testid="sender-cancel-btn">Cancel</button>
            <button
              onClick={submit}
              disabled={saving || !form.display_name || !form.email || (isNew && !form.key)}
              className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="sender-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {isNew ? 'Create' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TestModal = ({ profile, onClose }) => {
  const { adminInfo } = useApp();
  const [to, setTo] = useState(adminInfo?.email || '');
  const [sending, setSending] = useState(false);

  const run = async () => {
    if (!to) return toast.error('Enter a recipient email');
    try {
      setSending(true);
      await testSenderProfile(profile.id, to);
      toast.success(`Test sent from ${profile.display_name} to ${to}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed — check SMTP credentials');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="sender-test-modal">
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-[440px]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="text-white font-semibold">Send test from "{profile.display_name}"</div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          <Field label="Send test email to" help="The message will be sent through this profile's SMTP credentials">
            <TextInput
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="you@example.com"
              data-testid="sender-test-recipient-input"
            />
          </Field>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={run} disabled={sending} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="sender-test-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdminSenderProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null=closed, {} = new, {id...} = edit
  const [testing, setTesting] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await listSenderProfiles();
      setProfiles(r.data || []);
    } catch (e) {
      toast.error('Failed to load sender profiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSetDefault = async (p) => {
    try {
      await setDefaultSenderProfile(p.id);
      toast.success(`${p.display_name} is now the default sender`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set default');
    }
  };

  const handleDelete = async (p) => {
    const ok = window.confirm(`Delete sender profile "${p.display_name}"? Campaigns using it will be deactivated.`);
    if (!ok) return;
    try {
      const r = await deleteSenderProfile(p.id);
      if (r.data?.deactivated) {
        toast.success(`Profile deactivated (used by ${r.data.campaigns_using_profile} campaign${r.data.campaigns_using_profile === 1 ? '' : 's'})`);
      } else {
        toast.success('Sender profile deleted');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="p-6" data-testid="admin-sender-profiles-page">
      <Toaster position="top-right" theme="dark" />
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-5 h-5 text-[#00D4FF]" />
            <h1 className="text-2xl font-bold text-white">Sender Profiles</h1>
          </div>
          <p className="text-white/55 text-sm max-w-2xl">
            Configure the identities Luxor Tek uses to send email. Each profile sends through its own Hostinger mailbox so DKIM aligns properly and inboxes show the correct display name. The <span className="text-[#00D4FF]">default</span> profile is used for new campaigns when no specific sender is selected.
          </p>
        </div>
        <button onClick={() => setEditing({})} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="add-sender-profile-btn">
          <Plus className="w-4 h-4" /> New Profile
        </button>
      </div>

      <div className="border border-amber-400/20 bg-amber-400/5 p-3 mb-5 text-amber-200/90 text-xs flex items-start gap-2" data-testid="sender-profiles-tip">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          For best deliverability each mailbox must exist on Hostinger and have SPF/DKIM/DMARC configured for <span className="font-mono">luxortek.com</span>. Use the <span className="text-white">Test</span> button after entering credentials to verify each profile can actually send.
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 text-sm">Loading profiles…</div>
      ) : (
        <div className="space-y-3" data-testid="sender-profiles-list">
          {profiles.map((p) => (
            <ProfileRow
              key={p.id}
              p={p}
              onEdit={(x) => setEditing(x)}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
              onTest={(x) => setTesting(x)}
            />
          ))}
        </div>
      )}

      {editing !== null && (
        <EditModal
          profile={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {testing && <TestModal profile={testing} onClose={() => setTesting(null)} />}
    </div>
  );
};

export default AdminSenderProfiles;
